import { useEffect, useState, type FormEvent } from 'react';
import { kuunda } from '@/lib/kuunda';
import { preoccupationStatusLabel } from '@isbd/shared';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/features/ui/toast/useToast';

interface ThreadItem {
  id: string;
  subject: string;
  body: string;
  status: string;
  priority: string;
  created_at: string;
  replies: { id: string; subject?: string; body: string; created_at: string; kind: string }[];
}

const PRIORITY_LABEL: Record<string, string> = {
  low: 'Faible',
  medium: 'Moyenne',
  high: 'Importante',
  critical: 'Critique',
};

/** Trouve un admin/advisor pour recevoir la préoccupation. */
async function findStaffReceiver(): Promise<string | null> {
  const { data } = await kuunda
    .from('profiles')
    .select('id')
    .in('role', ['admin', 'advisor'])
    .eq('is_active', true)
    .limit(1)
    .maybeSingle();
  return data?.id ?? null;
}

export function PreoccupationsPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('medium');
  const [items, setItems] = useState<ThreadItem[]>([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    if (!profile) return;
    const { data: msgs, error: err } = await kuunda
      .from('messages')
      .select('id, subject, body, status, priority, created_at')
      .eq('sender_id', profile.id)
      .eq('is_preoccupation', true)
      .order('created_at', { ascending: false });
    if (err) {
      toast.error(err.message);
      return;
    }

    const threads: ThreadItem[] = [];
    for (const m of msgs ?? []) {
      const { data: notifs } = await kuunda
        .from('notifications')
        .select('id, subject, body, created_at, kind')
        .eq('user_id', profile.id)
        .eq('message_id', m.id)
        .eq('kind', 'preoccupation_reply')
        .order('created_at', { ascending: true });
      threads.push({
        id: m.id,
        subject: m.subject ?? '',
        body: m.body,
        status: m.status,
        priority: m.priority,
        created_at: m.created_at,
        replies: notifs ?? [],
      });
    }
    setItems(threads);
  }

  useEffect(() => {
    void load();
  }, [profile]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!profile || !subject.trim() || !body.trim()) return;
    setLoading(true);
    try {
      const receiverId = await findStaffReceiver();
      if (!receiverId) throw new Error('Aucun conseiller disponible pour recevoir votre message.');

      const { error: msgErr } = await kuunda
        .from('messages')
        .insert({
          sender_id: profile.id,
          receiver_id: receiverId,
          subject: subject.trim(),
          body: body.trim(),
          status: 'open',
          priority,
          is_preoccupation: true,
        });
      if (msgErr) throw msgErr;

      // La notification staff est créée côté DB (trigger notify_preoccupation_created).

      setSubject('');
      setBody('');
      toast.success('Préoccupation envoyée à l’équipe.');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Envoi impossible');
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(id: string, status: string) {
    const { error: err } = await kuunda.from('messages').update({ status }).eq('id', id);
    if (err) toast.error(err.message);
    else await load();
  }

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">Décrivez votre besoin ; l’équipe ISBD vous répondra dans ce fil.</p>
        </div>
        <article className="u-kpi" style={{ minHeight: 'auto', padding: '0.85rem 1.1rem' }}>
          <p className="u-kpi-label">Fils ouverts</p>
          <p className="u-kpi-value">{items.length}</p>
        </article>
      </div>

      <section className="u-card">
        <h3 className="u-section-title" style={{ marginTop: 0 }}>
          Nouveau message
        </h3>
        <form onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="preocc-subject">Objet</label>
            <input
              id="preocc-subject"
              required
              minLength={3}
              maxLength={120}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex. Difficulté sur le diagnostic"
            />
          </div>
          <div className="field">
            <label htmlFor="preocc-body">Message</label>
            <textarea
              id="preocc-body"
              required
              minLength={20}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="preocc-priority">Priorité</label>
            <select
              id="preocc-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <option value="low">Faible</option>
              <option value="medium">Moyenne</option>
              <option value="high">Importante</option>
              <option value="critical">Critique</option>
            </select>
          </div>
          <button className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? 'Envoi…' : 'Envoyer'}
          </button>
        </form>
      </section>

      {items.length === 0 ? (
        <p className="u-empty">Aucune préoccupation pour le moment.</p>
      ) : (
        items.map((t) => (
          <article key={t.id} className="u-card">
            <div className="u-card-head">
              <span className="chip">{PRIORITY_LABEL[t.priority] ?? t.priority}</span>
              <span className="muted">{new Date(t.created_at).toLocaleString('fr-FR')}</span>
            </div>
            {t.subject ? <h4 style={{ margin: '0.35rem 0 0.5rem' }}>{t.subject}</h4> : null}
            <p>{t.body}</p>
            <p>
              Statut : <strong>{preoccupationStatusLabel(t.status)}</strong>
            </p>
            <div className="u-actions">
              {(['open', 'in_progress', 'resolved', 'archived'] as const).map((s) => (
                <button key={s} type="button" className="btn btn-ghost" onClick={() => void updateStatus(t.id, s)}>
                  {preoccupationStatusLabel(s)}
                </button>
              ))}
            </div>
            {t.replies.length > 0 ? (
              <div style={{ marginTop: '0.85rem', paddingTop: '0.75rem', borderTop: '1px solid var(--line)' }}>
                <h4 style={{ margin: '0 0 0.5rem' }}>Réponses</h4>
                {t.replies.map((r) => (
                  <div key={r.id} style={{ marginBottom: '0.5rem' }}>
                    <p style={{ margin: 0 }}>{r.body}</p>
                    <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
                      {new Date(r.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted">En attente de réponse.</p>
            )}
          </article>
        ))
      )}
    </div>
  );
}
